import { useAuth } from "../../context/AuthContext";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import { Coins, Download, FileText } from "lucide-react";
import { Button } from "../../components/ui/button";
import { formatCurrency } from "../../lib/currency";
import { useMyPayslips } from "../../hooks";

const MyPayslips = () => {
  const { currentBusiness } = useAuth();
  const currency = currentBusiness?.currency || "INR";
  
  // Use custom hook for data fetching
  const { payslips, loading, stats, downloadPayslip } = useMyPayslips();

  return (
    <div className="space-y-6" data-testid="my-payslips-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-800 font-heading">My Payslips</h1>
        <p className="text-slate-500">View and download your salary slips</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="stat-card">
          <CardContent className="p-0">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-green-100"><Coins className="h-6 w-6 text-green-700" /></div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{stats.total}</p>
                <p className="text-sm text-slate-500">Total Payslips</p>
              </div>
            </div>
          </CardContent>
        </Card>
        {stats.latestPeriod && (
          <>
            <Card className="stat-card">
              <CardContent className="p-0">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-xl bg-primary/10"><FileText className="h-6 w-6 text-primary" /></div>
                  <div>
                    <p className="text-lg font-bold text-slate-800">{stats.latestPeriod}</p>
                    <p className="text-sm text-slate-500">Latest Period</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="stat-card">
              <CardContent className="p-0">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-xl bg-emerald-100"><Coins className="h-6 w-6 text-emerald-700" /></div>
                  <div>
                    <p className="text-lg font-bold text-slate-800">{formatCurrency(stats.latestNetPay, currency)}</p>
                    <p className="text-sm text-slate-500">Latest Net Pay</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Table */}
      <Card className="card-soft" data-testid="payslips-table">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" /></div>
          ) : payslips.length === 0 ? (
            <div className="text-center py-12 text-slate-500">No payslips available yet.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="text-xs uppercase tracking-wide text-slate-400">Period</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide text-slate-400">Gross Salary</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide text-slate-400">Deductions</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide text-slate-400">Net Salary</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide text-slate-400">Status</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide text-slate-400 text-right">Download</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payslips.map(p => (
                  <TableRow key={p.id} className="hover:bg-slate-50/50" data-testid={`payslip-row-${p.id}`}>
                    <TableCell className="font-medium text-slate-800">{p.month}/{p.year}</TableCell>
                    <TableCell className="text-slate-600">{formatCurrency(p.gross_salary, currency)}</TableCell>
                    <TableCell className="text-red-600">{formatCurrency(p.total_deductions, currency)}</TableCell>
                    <TableCell className="font-semibold text-slate-800">{formatCurrency(p.net_salary, currency)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={
                        p.status === "paid" ? "bg-green-50 text-green-700 border-green-200" :
                        p.status === "processed" ? "bg-blue-50 text-blue-700 border-blue-200" :
                        "bg-amber-50 text-amber-700 border-amber-200"
                      }>{p.status}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" className="rounded-full" onClick={() => downloadPayslip(p.id)}
                        data-testid={`download-payslip-${p.id}`}>
                        <Download className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MyPayslips;
